import { useIntl } from 'react-intl';
import { useCallback, useMemo } from 'react';

import { PointsStrategy, useReturnPurchasedVoucherMutation } from '@ocx/graphql';
import { useConfirmationActionSheet } from '@ocx/feat-modals';
import { messages } from '@ocx/ui-offers';
// eslint-disable-next-line @nx/enforce-module-boundaries -- app snackbar
import { useSnackbar } from '@ocx-app/lib/snackbar/snackbar';
// eslint-disable-next-line @nx/enforce-module-boundaries -- app snackbar helpers
import { useShowGenericErrorSnackbar } from '@ocx-app/hooks/useShowGenericErrorSnackbar';
// eslint-disable-next-line @nx/enforce-module-boundaries -- app configuration
import { useConfiguration } from '@ocx-app/modules/configuration/useConfiguration';

type UseReturnReward = (params: { onCompleted?: () => void }) => {
  returnReward: (offerId: string) => void;
  returningReward: boolean;
};
export const useReturnReward: UseReturnReward = ({ onCompleted }) => {
  const intl = useIntl();
  const snackbar = useSnackbar();
  const showGenericErrorSnackbar = useShowGenericErrorSnackbar();
  const { open: openConfirmationActionSheet } = useConfirmationActionSheet();

  const { pointsStrategy } = useConfiguration('loyaltyProgram');

  const [returnReward, { loading: isReturningReward }] = useReturnPurchasedVoucherMutation({
    refetchQueries: ['getOfferVoucher', 'offers', 'totalVouchersCount'],
  });

  const onSuccessfulReturn = useCallback(() => {
    snackbar.open(
      intl.formatMessage(
        pointsStrategy === PointsStrategy.Points
          ? messages.returnRewardForPointsSuccessText
          : messages.returnRewardForCentsSuccessText,
      ),
      { variant: 'success' },
    );
    onCompleted?.();
  }, [intl, onCompleted, pointsStrategy, snackbar]);

  const onReturnConfirmed = useCallback(
    async (offerId: string) => {
      return returnReward({
        variables: {
          input: { offerId },
        },
        onCompleted: onSuccessfulReturn,
        refetchQueries: ['currentCustomer', 'offers', 'totalVouchersCount'],
        onError: showGenericErrorSnackbar,
      });
    },
    [onSuccessfulReturn, returnReward, showGenericErrorSnackbar],
  );

  const openReturnRewardConfirmation = useCallback(
    (offerId: string) => {
      openConfirmationActionSheet({
        title: intl.formatMessage(messages.returnRewardConfirmationTitle),
        description: intl.formatMessage(messages.returnRewardConfirmationText),
        confirmButtonText: intl.formatMessage(messages.returnRewardConfirmationButtonTitle),
        onConfirm: () => onReturnConfirmed(offerId),
      });
    },
    [intl, onReturnConfirmed, openConfirmationActionSheet],
  );

  return useMemo(
    () => ({
      returnReward: openReturnRewardConfirmation,
      returningReward: isReturningReward,
    }),
    [isReturningReward, openReturnRewardConfirmation],
  );
};
